import React from 'react';
import { LayoutPanelTop } from 'lucide-react';

/**
 * Компонент для визуализации структуры бетона
 * Создает простую схематическую визуализацию формы конструкции
 */
const StructureVisualization = ({ formData, results }) => {
  if (!results) return null;

  // Получаем данные о форме
  const { shapeType } = formData;
  
  // Определяем простое SVG изображение в зависимости от типа формы
  const renderVisualization = () => {
    switch (shapeType) {
      case 'rectangle':
        return (
          <div className="flex justify-center">
            <svg width="300" height="200" viewBox="0 0 300 200" className="structure-svg">
              <defs>
                <linearGradient id="rectangleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4299e1" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#2b6cb0" stopOpacity="0.7" />
                </linearGradient>
              </defs>
              {/* 3D прямоугольник */}
              <polygon points="50,50 250,50 250,150 50,150" fill="url(#rectangleGradient)" stroke="#2c5282" strokeWidth="2" />
              <polygon points="250,50 270,30 270,130 250,150" fill="#63b3ed" stroke="#2c5282" strokeWidth="2" />
              <polygon points="50,150 70,130 270,130 250,150" fill="#4299e1" stroke="#2c5282" strokeWidth="2" />
              <polygon points="250,50 270,30 70,30 50,50" fill="#90cdf4" stroke="#2c5282" strokeWidth="2" />
              <text x="150" y="100" textAnchor="middle" fill="#fff" fontWeight="bold" fontSize="16">
                {results.totalVolume.toFixed(2)} м³
              </text>
              <text x="150" y="120" textAnchor="middle" fill="#fff" fontSize="12">
                {`${formData.length} × ${formData.width} × ${formData.height} м`}
              </text>
            </svg>
          </div>
        );

      case 'cylinder':
        return (
          <div className="flex justify-center">
            <svg width="300" height="200" viewBox="0 0 300 200" className="structure-svg">
              <defs>
                <linearGradient id="cylinderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4299e1" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#2b6cb0" stopOpacity="0.7" />
                </linearGradient>
              </defs>
              {/* Цилиндр */}
              <ellipse cx="150" cy="50" rx="80" ry="30" fill="#90cdf4" stroke="#2c5282" strokeWidth="2" />
              <rect x="70" y="50" width="160" height="100" fill="url(#cylinderGradient)" />
              <ellipse cx="150" cy="150" rx="80" ry="30" fill="#4299e1" stroke="#2c5282" strokeWidth="2" />
              <text x="150" y="110" textAnchor="middle" fill="#fff" fontWeight="bold" fontSize="16">
                {results.totalVolume.toFixed(2)} м³
              </text>
              <text x="150" y="130" textAnchor="middle" fill="#fff" fontSize="12">
                {`∅ ${formData.diameter || (formData.radius*2)} × ${formData.height} м`}
              </text>
            </svg>
          </div>
        );

      case 'l_shape':
        return (
          <div className="flex justify-center">
            <svg width="300" height="200" viewBox="0 0 300 200" className="structure-svg">
              <defs>
                <linearGradient id="lShapeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4299e1" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#2b6cb0" stopOpacity="0.7" />
                </linearGradient>
              </defs>
              {/* L-образная форма (вид сверху) */}
              <polygon 
                points="50,50 200,50 200,100 100,100 100,150 50,150" 
                fill="url(#lShapeGradient)" 
                stroke="#2c5282" 
                strokeWidth="2" 
              />
              <text x="125" y="80" textAnchor="middle" fill="#fff" fontWeight="bold" fontSize="16">
                {results.totalVolume.toFixed(2)} м³
              </text>
              <text x="125" y="100" textAnchor="middle" fill="#fff" fontSize="10">
                L-образная форма
              </text>
            </svg>
          </div>
        );

      case 'arbitrary':
        return (
          <div className="flex justify-center">
            <svg width="300" height="200" viewBox="0 0 300 200" className="structure-svg">
              <defs>
                <linearGradient id="customGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4299e1" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#2b6cb0" stopOpacity="0.7" />
                </linearGradient>
              </defs>
              {/* Условное представление произвольной формы */}
              <rect x="20" y="50" width="80" height="80" fill="url(#customGradient)" stroke="#2c5282" strokeWidth="2" />
              <rect x="110" y="50" width="60" height="50" fill="url(#customGradient)" stroke="#2c5282" strokeWidth="2" />
              <rect x="180" y="80" width="100" height="40" fill="url(#customGradient)" stroke="#2c5282" strokeWidth="2" />
              <rect x="110" y="110" width="40" height="60" fill="url(#customGradient)" stroke="#2c5282" strokeWidth="2" />
              <text x="150" y="170" textAnchor="middle" fill="#3182ce" fontWeight="bold" fontSize="16">
                {results.totalVolume.toFixed(2)} м³
              </text>
              <text x="150" y="190" textAnchor="middle" fill="#3182ce" fontSize="12">
                {`${formData.customSections.length} секций`}
              </text>
            </svg>
          </div>
        );

      default:
        return (
          <div className="flex justify-center text-center py-8 text-gray-500 dark:text-gray-400">
            Визуализация для данной формы недоступна
          </div>
        );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <LayoutPanelTop className="w-5 h-5 mr-2 text-cyan-500" />
        Визуализация конструкции
      </h3>

      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        {renderVisualization()}
      </div>

      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        <p>Данная визуализация является схематичной и предназначена для общего представления о форме и размерах конструкции.</p>
      </div>
    </div>
  );
};

export default StructureVisualization;